import { createServerFn } from '@tanstack/react-start'
import { z } from 'zod'
import { getMergedProductById } from '#bv/lib/productCatalog'
import { assertProductsInStockOrThrow } from '#bv/lib/productStock'
import {
  getCryptoLedgerPayment,
  insertCryptoLedgerPayment,
  type CryptoLedgerItem,
  updateCryptoLedgerPayment,
} from '#bv/lib/cryptoPaymentLedger'
import { cryptoConfirmationsRequired, parseShkeeperInvoice } from '#bv/lib/cryptoPaymentDisplay'
import { normalizeCryptoProcessorMessage, parseBuyerEmailFromCheckoutPayload } from '#bv/lib/buyerEmail'

const SHKEEPER_DEFAULT = 'https://pay.blkvault.cc'

function getApiKey(): string | undefined {
  return process.env.SHKEEPER_API_KEY?.trim()
}

function getShkeeperBase(): string {
  const raw = (process.env.SHKEEPER_BASE_URL ?? SHKEEPER_DEFAULT).trim()
  try {
    return new URL(raw.startsWith('http') ? raw : `https://${raw}`).origin
  } catch {
    return SHKEEPER_DEFAULT
  }
}

function siteOrigin(): string {
  const raw = (process.env.SITE_URL ?? 'http://localhost:3000').trim()
  try {
    return new URL(raw).origin
  } catch {
    const withProtocol = raw.startsWith('http://') || raw.startsWith('https://') ? raw : `https://${raw}`
    try {
      return new URL(withProtocol).origin
    } catch {
      return 'http://localhost:3000'
    }
  }
}

async function npFetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const key = getApiKey()
  if (!key) throw new Error('Crypto processor is not configured (set SHKEEPER_API_KEY).')
  const headers: Record<string, string> = { 'X-Shkeeper-Api-Key': key }
  if (init?.body != null) headers['Content-Type'] = 'application/json'
  const res = await fetch(`${getShkeeperBase()}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) },
  })
  const text = await res.text()
  if (!res.ok) {
    throw new Error(`Crypto processor ${res.status}: ${text.slice(0, 400)}`)
  }
  try {
    return JSON.parse(text) as T
  } catch {
    throw new Error('Crypto processor returned non-JSON.')
  }
}

export const getNowPaymentsEnabled = createServerFn({ method: 'GET' }).handler(() => !!getApiKey())

const createPaymentInputSchema = z.object({
  productId: z.number().int().positive(),
  email: z.string(),
  payCurrency: z.string().trim().min(2).max(16).optional(),
})

export type { CryptoPaymentTxPublic } from '#bv/lib/cryptoPaymentDisplay'

export type NowPaymentPublic = {
  paymentId: string
  paymentStatus: string
  payAddress: string
  payAmount: number | null
  payCurrency: string
  priceAmount: number
  priceCurrency: string
  orderId: string
  orderDescription: string
  createdAt: string | null
  updatedAt: string | null
  /** Expected crypto amount (invoice). */
  expectedCrypto: number | null
  /** Crypto received so far (SHKeeper balance_crypto). */
  receivedCrypto: number | null
  fiatReceived: number | null
  transactions: CryptoPaymentTxPublic[]
  confirmationsRequired: number
  confirmationsCurrent: number
  invoiceStatusRaw: string
}

type ShkeeperCreateResponse = {
  amount?: string | number
  wallet?: string
  display_name?: string
  status?: string
  id?: number
  exchange_rate?: string | number
  recalculate_after?: number
}

function mapLedgerStatusToCheckoutStatus(status: string): string {
  const s = status.trim().toLowerCase()
  if (s === 'paid' || s === 'overpaid' || s === 'complete' || s === 'completed') return 'finished'
  if (s === 'partial') return 'confirming'
  if (s === 'failed' || s === 'expired' || s === 'refunded') return 'failed'
  if (s === 'unpaid' || s === 'waiting' || !s) return 'waiting'
  return s
}

function mapShkeeperInvoiceStatus(raw: string): string {
  const s = raw.trim().toUpperCase()
  if (s === 'PAID' || s === 'OVERPAID') return 'finished'
  if (s === 'PARTIAL') return 'confirming'
  if (s === 'UNPAID' || !s) return 'waiting'
  return mapLedgerStatusToCheckoutStatus(raw)
}

function emptyChainFields(
  payCurrency: string,
  expectedCrypto: number | null,
): Pick<
  NowPaymentPublic,
  | 'expectedCrypto'
  | 'receivedCrypto'
  | 'fiatReceived'
  | 'transactions'
  | 'confirmationsRequired'
  | 'confirmationsCurrent'
  | 'invoiceStatusRaw'
> {
  return {
    expectedCrypto,
    receivedCrypto: null,
    fiatReceived: null,
    transactions: [],
    confirmationsRequired: cryptoConfirmationsRequired(payCurrency),
    confirmationsCurrent: 0,
    invoiceStatusRaw: '',
  }
}

function mapLedgerRowToPublic(
  row: {
    paymentId: string
    paymentStatus: string
    payAddress: string
    payAmount: number | null
    payCurrency: string
    totalUsd: number
    createdAt: string
    updatedAt: string
  },
  chain?: Partial<
    Pick<
      NowPaymentPublic,
      | 'expectedCrypto'
      | 'receivedCrypto'
      | 'fiatReceived'
      | 'transactions'
      | 'confirmationsRequired'
      | 'confirmationsCurrent'
      | 'invoiceStatusRaw'
      | 'paymentStatus'
    >
  >,
): NowPaymentPublic {
  const payCurrency = row.payCurrency.toLowerCase()
  const defaults = emptyChainFields(payCurrency, row.payAmount)
  return {
    paymentId: row.paymentId,
    paymentStatus: chain?.paymentStatus ?? mapLedgerStatusToCheckoutStatus(row.paymentStatus),
    payAddress: row.payAddress,
    payAmount: row.payAmount,
    payCurrency,
    priceAmount: row.totalUsd,
    priceCurrency: 'usd',
    orderId: row.paymentId,
    orderDescription: '',
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
    expectedCrypto: chain?.expectedCrypto ?? defaults.expectedCrypto,
    receivedCrypto: chain?.receivedCrypto ?? defaults.receivedCrypto,
    fiatReceived: chain?.fiatReceived ?? defaults.fiatReceived,
    transactions: chain?.transactions ?? defaults.transactions,
    confirmationsRequired: chain?.confirmationsRequired ?? defaults.confirmationsRequired,
    confirmationsCurrent: chain?.confirmationsCurrent ?? defaults.confirmationsCurrent,
    invoiceStatusRaw: chain?.invoiceStatusRaw ?? defaults.invoiceStatusRaw,
  }
}

function nowIso() {
  return new Date().toISOString()
}

async function persistLedger(mapped: NowPaymentPublic, ctx: { internalOrderId: string; email: string; items: CryptoLedgerItem[] }) {
  if (!mapped.paymentId) return
  const totalUsd = ctx.items.reduce((s, i) => s + i.unitPrice * i.quantity, 0)
  try {
    await insertCryptoLedgerPayment({
      paymentId: mapped.paymentId,
      internalOrderId: ctx.internalOrderId,
      buyerEmail: ctx.email,
      totalUsd: Math.round(totalUsd * 100) / 100,
      items: ctx.items,
      payCurrency: mapped.payCurrency || 'ltc',
      payAddress: mapped.payAddress,
      payAmount: mapped.payAmount,
      paymentStatus: mapped.paymentStatus,
      createdAt: nowIso(),
      updatedAt: nowIso(),
      notifiedFinished: false,
      notifiedTerminalFailure: false,
    })
  } catch (e) {
    console.error('[NOWPayments] ledger insert failed', e)
  }
}

function mapPayCurrency(input?: string): string {
  const c = (input ?? 'ltc').trim().toLowerCase()
  if (c === 'btc') return 'BTC'
  if (c === 'eth') return 'ETH'
  if (c === 'sol') return 'SOL'
  if (c === 'doge') return 'DOGE'
  if (c === 'usdt') return 'ETH-USDT'
  return 'LTC'
}

/** Create a SHKeeper crypto invoice (deposit address + amount) for one product. */
export const createNowPaymentsPayment = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => createPaymentInputSchema.parse(d))
  .handler(async ({ data }) => {
    const email = parseBuyerEmailFromCheckoutPayload(data.email)
    const product = await getMergedProductById(data.productId)
    if (!product) throw new Error('Product not found')
    await assertProductsInStockOrThrow([{ productId: product.id, name: product.name }])

    const ipnBase = siteOrigin()
    const ipnUrl = `${ipnBase}/api/shkeeper-callback`
    const orderRef = `bv-${data.productId}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
    const payCurrency = mapPayCurrency(data.payCurrency)

    const body = {
      external_id: orderRef,
      fiat: 'USD',
      amount: Number(product.price.toFixed(2)).toFixed(2),
      callback_url: ipnUrl,
    }

    const row = await npFetchJson<ShkeeperCreateResponse>(`/api/v1/${encodeURIComponent(payCurrency)}/payment_request`, {
      method: 'POST',
      body: JSON.stringify(body),
    })

    if (row.status === 'error' || (!row.wallet && !row.amount)) {
      const msg = (row as Record<string, unknown>).message
      throw new Error(
        normalizeCryptoProcessorMessage(
          typeof msg === 'string' ? msg : 'Crypto processor did not return a wallet address.',
        ),
      )
    }

    const now = nowIso()
    const parsedAmount = typeof row.amount === 'number' ? row.amount : Number(row.amount)
    const payCur = payCurrency.toLowerCase()
    const payAmt = Number.isFinite(parsedAmount) ? parsedAmount : null
    const mapped: NowPaymentPublic = {
      paymentId: orderRef,
      paymentStatus: 'waiting',
      payAddress: String(row.wallet ?? ''),
      payAmount: payAmt,
      payCurrency: payCur,
      priceAmount: Number(product.price.toFixed(2)),
      priceCurrency: 'usd',
      orderId: orderRef,
      orderDescription: `${product.name} — buyer: ${email}`.slice(0, 512),
      createdAt: now,
      updatedAt: now,
      ...emptyChainFields(payCur, payAmt),
    }
    await persistLedger(mapped, {
      internalOrderId: orderRef,
      email,
      items: [{ productId: product.id, name: product.name, unitPrice: product.price, quantity: 1 }],
    })
    return mapped
  })

const cartLineSchema = z.object({
  productId: z.number().int().positive(),
  name: z.string().min(1),
  unitPrice: z.number().nonnegative(),
  quantity: z.number().int().positive().max(100),
  smmPackageQuantity: z.number().int().positive().optional(),
  smmTiktokTarget: z.string().trim().optional(),
  smmServiceId: z.number().int().positive().optional(),
  smmTierId: z.string().trim().optional(),
  robuxAmount: z.number().int().positive().optional(),
  robuxGamepassPrice: z.number().int().positive().optional(),
  robuxGamepassLink: z.string().trim().min(12).max(512).optional(),
  serverBoostCount: z.number().int().positive().optional(),
  serverBoostInviteLink: z.string().trim().min(12).max(512).optional(),
})

const createCartCryptoSchema = z.object({
  items: z.array(cartLineSchema).min(1).max(50),
  email: z.string(),
  payCurrency: z.string().trim().min(2).max(16).optional(),
})

/** Multi-line cart -> one SHKeeper invoice (USD total). */
export const createNowPaymentsCartPayment = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => createCartCryptoSchema.parse(d))
  .handler(async ({ data }) => {
    const email = parseBuyerEmailFromCheckoutPayload(data.email)
    await assertProductsInStockOrThrow(
      data.items.map((item) => ({ productId: item.productId, name: item.name })),
    )
    const { getMergedProductById } = await import('#bv/lib/productCatalog')
    const { assertSoloCustomCheckoutLine } = await import('#bv/lib/customQuantityCheckout')
    assertSoloCustomCheckoutLine(data.items, 'Robux, Server Boost, and growth packages')
    const { validateRobuxCartLine } = await import('#bv/lib/robuxProduct')
    const robuxLines = data.items.filter((i) => i.robuxAmount != null)
    if (robuxLines.length > 0) {
      const line = robuxLines[0]!
      const product = await getMergedProductById(line.productId)
      const err = validateRobuxCartLine(line, product)
      if (err) throw new Error(err)
    }
    const { validateServerBoostCartLine } = await import('#bv/lib/serverBoostProduct')
    const boostLines = data.items.filter((i) => i.serverBoostCount != null)
    if (boostLines.length > 0) {
      const line = boostLines[0]!
      const product = await getMergedProductById(line.productId)
      const err = validateServerBoostCartLine(line, product)
      if (err) throw new Error(err)
    }
    const totalUsd = data.items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0)
    const priceAmount = Math.max(0.01, Math.round(totalUsd * 100) / 100)

    const ipnBase = siteOrigin()
    const ipnUrl = `${ipnBase}/api/shkeeper-callback`
    const orderRef = `bv-cart-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    const desc = `Cart (${data.items.length} items) — ${email}`.slice(0, 512)
    const payCurrency = mapPayCurrency(data.payCurrency)

    const body = {
      external_id: orderRef,
      fiat: 'USD',
      amount: priceAmount.toFixed(2),
      callback_url: ipnUrl,
    }

    const row = await npFetchJson<ShkeeperCreateResponse>(`/api/v1/${encodeURIComponent(payCurrency)}/payment_request`, {
      method: 'POST',
      body: JSON.stringify(body),
    })

    if (row.status === 'error' || (!row.wallet && !row.amount)) {
      const msg = (row as Record<string, unknown>).message
      throw new Error(
        normalizeCryptoProcessorMessage(
          typeof msg === 'string' ? msg : 'Crypto processor did not return a wallet address.',
        ),
      )
    }

    const now = nowIso()
    const parsedCartAmount = typeof row.amount === 'number' ? row.amount : Number(row.amount)
    const payCur = payCurrency.toLowerCase()
    const payAmt = Number.isFinite(parsedCartAmount) ? parsedCartAmount : null
    const mapped: NowPaymentPublic = {
      paymentId: orderRef,
      paymentStatus: 'waiting',
      payAddress: String(row.wallet ?? ''),
      payAmount: payAmt,
      payCurrency: payCur,
      priceAmount,
      priceCurrency: 'usd',
      orderId: orderRef,
      orderDescription: desc,
      createdAt: now,
      updatedAt: now,
      ...emptyChainFields(payCur, payAmt),
    }
    await persistLedger(mapped, {
      internalOrderId: orderRef,
      email,
      items: data.items.map((i) => ({
        productId: i.productId,
        name: i.name,
        unitPrice: i.unitPrice,
        quantity: i.quantity,
        ...(i.smmPackageQuantity != null ? { smmPackageQuantity: i.smmPackageQuantity } : {}),
        ...(i.smmTiktokTarget ? { smmTiktokTarget: i.smmTiktokTarget } : {}),
        ...(i.smmServiceId != null ? { smmServiceId: i.smmServiceId } : {}),
        ...(i.smmTierId ? { smmTierId: i.smmTierId } : {}),
        ...(i.robuxAmount != null ? { robuxAmount: i.robuxAmount } : {}),
        ...(i.robuxGamepassPrice != null ? { robuxGamepassPrice: i.robuxGamepassPrice } : {}),
        ...(i.robuxGamepassLink ? { robuxGamepassLink: i.robuxGamepassLink } : {}),
        ...(i.serverBoostCount != null ? { serverBoostCount: i.serverBoostCount } : {}),
        ...(i.serverBoostInviteLink ? { serverBoostInviteLink: i.serverBoostInviteLink } : {}),
      })),
    })
    return mapped
  })

const createDonationCryptoSchema = z.object({
  amountUsd: z.number().finite().positive().max(5000),
  email: z.string().min(3).max(320),
  note: z.string().trim().max(400).optional(),
  payCurrency: z.string().trim().min(2).max(16).optional(),
})

/** Donation -> one SHKeeper invoice (USD amount). */
export const createNowPaymentsDonationPayment = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => createDonationCryptoSchema.parse(d))
  .handler(async ({ data }) => {
    const email = parseBuyerEmailFromCheckoutPayload(data.email)
    const amountUsd = Math.round(Math.max(0.01, data.amountUsd) * 100) / 100

    const ipnBase = siteOrigin()
    const ipnUrl = `${ipnBase}/api/shkeeper-callback`
    const orderRef = `bv-donate-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    const payCurrency = mapPayCurrency(data.payCurrency)
    const note = data.note?.trim() || ''
    const desc = `Donation — ${email}${note ? ` — ${note}` : ''}`.slice(0, 512)

    const body = {
      external_id: orderRef,
      fiat: 'USD',
      amount: amountUsd.toFixed(2),
      callback_url: ipnUrl,
    }

    const row = await npFetchJson<ShkeeperCreateResponse>(`/api/v1/${encodeURIComponent(payCurrency)}/payment_request`, {
      method: 'POST',
      body: JSON.stringify(body),
    })

    if (row.status === 'error' || (!row.wallet && !row.amount)) {
      const msg = (row as Record<string, unknown>).message
      throw new Error(
        normalizeCryptoProcessorMessage(
          typeof msg === 'string' ? msg : 'Crypto processor did not return a wallet address.',
        ),
      )
    }

    const now = nowIso()
    const parsedCartAmount = typeof row.amount === 'number' ? row.amount : Number(row.amount)
    const payCur = payCurrency.toLowerCase()
    const payAmt = Number.isFinite(parsedCartAmount) ? parsedCartAmount : null
    const mapped: NowPaymentPublic = {
      paymentId: orderRef,
      paymentStatus: 'waiting',
      payAddress: String(row.wallet ?? ''),
      payAmount: payAmt,
      payCurrency: payCur,
      priceAmount: amountUsd,
      priceCurrency: 'usd',
      orderId: orderRef,
      orderDescription: desc,
      createdAt: now,
      updatedAt: now,
      ...emptyChainFields(payCur, payAmt),
    }

    await persistLedger(mapped, {
      internalOrderId: orderRef,
      email,
      items: [{ productId: 999999, name: 'Donation', unitPrice: amountUsd, quantity: 1 }],
    })

    return mapped
  })

const paymentIdSchema = z.object({
  paymentId: z.string().min(1).max(64),
})

export const getNowPaymentsPayment = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => paymentIdSchema.parse(d))
  .handler(async ({ data }) => {
    const ledger = await getCryptoLedgerPayment(data.paymentId)
    if (!ledger) {
      throw new Error('Payment not found.')
    }
    const externalId = encodeURIComponent(data.paymentId)
    try {
      const info = await npFetchJson<{ invoices?: Array<Record<string, unknown>> }>(`/api/v1/invoices/${externalId}`, {
        method: 'GET',
      })
      const invoice = Array.isArray(info.invoices) ? info.invoices[0] : null
      if (invoice) {
        const expectedCrypto = ledger.payAmount
        const chain = parseShkeeperInvoice(invoice, ledger.payCurrency, expectedCrypto)
        const status = mapShkeeperInvoiceStatus(
          chain.invoiceStatusRaw || String(invoice.status ?? ledger.paymentStatus),
        )

        let addr = typeof invoice.addr === 'string' && invoice.addr ? invoice.addr : ''
        if (!addr && Array.isArray(invoice.txs)) {
          for (const tx of invoice.txs) {
            if (tx && typeof tx === 'object' && typeof (tx as Record<string, unknown>).addr === 'string') {
              addr = (tx as Record<string, unknown>).addr as string
              break
            }
          }
        }

        const next = {
          ...ledger,
          paymentStatus: status,
          payAmount: expectedCrypto,
          payAddress: addr || ledger.payAddress,
          updatedAt: nowIso(),
        }
        await updateCryptoLedgerPayment(next)
        return mapLedgerRowToPublic(next, { ...chain, paymentStatus: status })
      }
    } catch {
      // fallback to ledger snapshot if SHKeeper status polling is unavailable
    }
    return mapLedgerRowToPublic(ledger)
  })

export const getCryptoLedgerSummary = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => paymentIdSchema.parse(d))
  .handler(async ({ data }) => {
    const { getCryptoLedgerPayment } = await import('#bv/lib/cryptoPaymentLedger')
    const row = await getCryptoLedgerPayment(data.paymentId)
    if (!row) return null
    return {
      items: row.items,
      buyerEmail: row.buyerEmail,
      totalUsd: row.totalUsd,
      internalOrderId: row.internalOrderId,
    }
  })
